import { Zap, MessageSquare, History, CreditCard, Sparkles } from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';
import { formatDistanceToNow } from 'date-fns';
import { ko } from 'date-fns/locale';

export interface CreditTransaction {
  id: string;
  transaction_type: string;
  amount: number;
  description: string;
  created_at: string;
}

interface CreditTransactionsListProps {
  transactions: CreditTransaction[];
  isLoading?: boolean;
}

export function CreditTransactionsList({ transactions, isLoading = false }: CreditTransactionsListProps) {
  const getTimeAgo = (dateString: string) => {
    try {
      const date = new Date(dateString);
      return formatDistanceToNow(date, { addSuffix: true, locale: ko });
    } catch (e) {
      return '기록 없음';
    }
  };

  const getTransactionTypeText = (type: string) => {
    switch (type) {
      case 'add': return '충전';
      case 'deduct': return '사용';
      case 'expire': return '만료';
      case 'refund': return '환불';
      default: return type;
    }
  };

  const getTransactionIcon = (type: string, description: string) => {
    if (type === 'add') {
      if (description.includes('구독') || description.includes('subscription')) {
        return <Sparkles className="h-4 w-4" />;
      } else if (description.includes('결제') || description.includes('payment')) {
        return <CreditCard className="h-4 w-4" />;
      } else {
        return <Zap className="h-4 w-4" />;
      }
    } else if (type === 'deduct') {
      return <MessageSquare className="h-4 w-4" />;
    } else {
      return <History className="h-4 w-4" />;
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-2">
        {[1, 2, 3].map((i) => (
          <div key={i} className="flex items-center gap-3 rounded-md border p-3">
            <Skeleton className="h-8 w-8 rounded-full" />
            <div className="flex-1 space-y-1">
              <Skeleton className="h-4 w-24" />
              <Skeleton className="h-3 w-32" />
            </div>
            <Skeleton className="h-4 w-12" />
          </div>
        ))}
      </div>
    );
  }

  if (transactions.length === 0) {
    return (
      <div className="rounded-lg border p-6 text-center text-muted-foreground">
        <History className="mx-auto h-8 w-8 mb-2 opacity-50" />
        <p>아직 거래 내역이 없습니다.</p>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {transactions.map((transaction) => (
        <div 
          key={transaction.id} 
          className="flex items-center justify-between rounded-md border p-3 transition-all hover:bg-muted/30"
        >
          <div className="flex items-start gap-3">
            <div className={`rounded-full p-2 ${
              transaction.transaction_type === 'add' 
                ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' 
                : 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
            }`}>
              {getTransactionIcon(transaction.transaction_type, transaction.description)}
            </div>
            <div>
              <p className="font-medium">
                {getTransactionTypeText(transaction.transaction_type)}
              </p>
              <p className="text-xs text-muted-foreground">
                {transaction.description}
              </p>
              <p className="text-xs text-muted-foreground">
                {getTimeAgo(transaction.created_at)}
              </p>
            </div>
          </div>
          <div className={`font-semibold ${
            transaction.transaction_type === 'add' 
              ? 'text-green-600 dark:text-green-400' 
              : 'text-red-600 dark:text-red-400'
          }`}>
            {transaction.transaction_type === 'add' ? '+' : '-'}{transaction.amount}
          </div>
        </div>
      ))}
    </div>
  );
}